import { Injectable } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { environment } from '../../environments/environment';
import { inflate } from 'zlib';

import { Setting } from '../models';

import { AuthenticationService } from './authentication.service';
import { CredentialService } from './credential.service';

import { allSettingFields } from '../../ngrx/graphql';
import { SET_MAIN_VIEW } from 'ngrx/actions';

const errorCodes = [400, 401, 403, 404];

@Injectable()
export class SettingService {
  constructor(
    private authService: AuthenticationService,
    private credentialService: CredentialService,
    private store: Store<any>
  ) {}

  private invokeSettings(payload: any): Observable<any> {
    return new Observable(observer => {
      this.credentialService.lambda.invoke({
        FunctionName: environment.settingFunction,
        Payload: JSON.stringify(payload)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
              // this.authService.logout();
            }
            observer.error(err);
          } else {
            const output = typeof data.Payload === 'string'
                ? JSON.parse(data.Payload)
                :       data.Payload;
            if(output.gzip){
              const options = {
                windowBits : 15
              };
              inflate(new Buffer(output.body,'base64'), options, (err, data) => {
                if (err) {                    
                  observer.error(err.stack);
                } else {
                  const unzippedOutput = Object.assign({}, output, {
                    body: data.toString()
                  });
                  observer.next(unzippedOutput);
                }
              });
            } else {
              observer.next(output);
            }
          }
        }
      )
    });
  }

  private invokeSettingMutation(payload: any): void {
    this.credentialService.lambda.invoke({
      FunctionName: environment.settingFunction,
      Payload: JSON.stringify(payload)
      }, (err, data) => {
        if (err) {
          if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
            this.authService.refreshCredentials();
              // this.authService.logout();
          }
        }
      }
    );
  }

  createSetting(setting: Setting): void {
    const variables = { setting };
    const query = `mutation ($setting: SettingInput!) {
      createSetting(setting: $setting) {
        ${allSettingFields}
      }
    }`;

    this.invokeSettingMutation({ query, variables });
  }

  deleteSetting(id: string): void {
    const variables = { id };
    const query = `mutation ($id: ID!) {
      deleteSetting(id: $id) {
        ${allSettingFields}
      }
    }`;

    this.invokeSettingMutation({ query, variables });
  }

  getNextBusinessDay(): Observable<string> {
    return this.store.select('settings', 'nextBusinessDay');
  }

  getSetting(id: string): Observable<any> {
    const variables = { id };
    const query = `{
      setting(id: "${id}") {
        ${allSettingFields}
      }
    }`;

    return this.invokeSettings({ query });
  }

  getSettingList(): Observable<Setting[]> {
    return this.store.select('settings', 'systemSettingList');
  }

  getSettings(): Observable<any> {
    const query = `{
      settings(param: "all") {
        ${allSettingFields}
      }
    }`;

    return this.invokeSettings({ query });
  }

  updateSetting(setting: Setting): void {
    console.log(`updating setting: ${JSON.stringify(setting, null, 2)}`);
    if (setting) {
      const variables = { setting };
      const query = `mutation ($setting: SettingInput!) {
        updateSetting(setting: $setting) {
          ${allSettingFields}
        }
      }`;

      this.invokeSettingMutation({ query, variables });
    }
  }

  updateSettings(settings: Setting[]): void {
    console.log(`updating settings: ${JSON.stringify(settings, null, 2)}`);
    if (settings && settings.length) {
      const variables = { settings };
      const query = `mutation ($settings: [SettingInput]) {
        updateSettings(settings: $settings) {
          ${allSettingFields}
        }
      }`;

      this.invokeSettingMutation({ query, variables });
    }
  }

  getShippingConfig(): any {
    const shippingConfig = localStorage.getItem('shippingConfig');

    if (!shippingConfig || !Object.keys(shippingConfig).length || shippingConfig == 'null') {
      localStorage.setItem('shippingConfig', null);
      return null;
    } else {
      return shippingConfig;
    }
  }

  setShippingConfig(shipping: any = ''): void {
    localStorage.setItem('shippingConfig', shipping);
    this.store.dispatch({
      type: SET_MAIN_VIEW,
      payload: { shippingConfig: shipping }
    });
  }
}


// WEBPACK FOOTER //
// /Users/itu/Desktop/impreso_updated_sheets/impresocloud/www/client/src/app/services/setting.service.ts